import { useCallback, useEffect, useRef, useState } from 'react';

import { useExportFile } from '../queries/uploadedfiles-query';
import {
  buildExportPayload,
  type BuildExportPayloadInput,
} from '../utils/export/buildExportPayload';
import { downloadFile } from '../utils/file-utils';
import { toast } from '../utils/toast';
import { isSafeUrl } from '../utils/url-validation';

export interface ExportStatus {
  message: string;
  severity: 'success' | 'error' | 'info';
}

export const EXPORT_STATUS = {
  inProgress: { message: 'Export in progress', severity: 'info' },
  success: { message: 'Export ready', severity: 'success' },
  completed: { message: 'Export completed', severity: 'success' },
  failed: { message: 'Export failed', severity: 'error' },
} as const satisfies Record<string, ExportStatus>;

/**
 * One key per export, so "in progress" is *replaced* by its outcome rather than
 * stacking a second card that contradicts the first.
 *
 * Keyed per hook instance, not per module: toasts outlive the page that raised
 * them, so a shared key would let a lingering export on the page you just left
 * overwrite the one you started on the page you are now on.
 */
export const EXPORT_TOAST_KEY = 'list-export';
let exportInstances = 0;

const report = (status: ExportStatus, dedupeKey: string): void => {
  toast[status.severity](status.message, { dedupeKey });
};

/** Shown when the BFF reports a large export was emailed but sends no message
 *  of its own. */
export const EXPORT_EMAILED_FALLBACK =
  'This export is too large to download. It will be emailed to you when it is ready.';

export type UseListExportOptions = Omit<BuildExportPayloadInput, 'fileType'>;

export interface UseListExportReturn {
  /** Anchor for the page's Export menu. Cleared automatically on export. */
  exportAnchor: HTMLElement | null;
  setExportAnchor: (element: HTMLElement | null) => void;
  handleExport: (fileType: 'csv' | 'xlsx') => Promise<void>;
  isExporting: boolean;
}

/**
 * The single export pipeline for record list pages: build the payload from the
 * on-screen state, POST it, and download what comes back.
 *
 * Every list page used to hand-roll this. The copies drifted, and exports
 * stopped matching the columns, filters and search the user could see. Keeping
 * one implementation is what makes the shared export contract suite meaningful
 * — see `src/test-utils/exportContract`.
 */
export const useListExport = (options: UseListExportOptions): UseListExportReturn => {
  const exportMutation = useExportFile();
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);

  // Latest-value ref so `handleExport` stays referentially stable across the
  // renders that page state changes cause — list headers take it as a prop.
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  const { mutateAsync } = exportMutation;

  // Guards against a second export starting while one is in flight — a
  // double-click would otherwise POST twice and download the file twice. A ref
  // rather than the mutation's `isPending`, because two clicks in the same tick
  // both read the pre-render value of that flag.
  const inFlightRef = useRef(false);

  // Assigned once per mounted hook, and stable across this instance's exports.
  const toastKeyRef = useRef('');
  if (!toastKeyRef.current) {
    exportInstances += 1;
    toastKeyRef.current = `${EXPORT_TOAST_KEY}-${exportInstances}`;
  }

  const handleExport = useCallback(
    async (fileType: 'csv' | 'xlsx') => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const input = optionsRef.current;
      setExportAnchor(null);
      report(EXPORT_STATUS.inProgress, toastKeyRef.current);
      try {
        const result = await mutateAsync(buildExportPayload({ ...input, fileType }));

        // Large exports are delivered by email rather than as a download.
        if (result?.sendEmail) {
          report(
            { message: result.message || EXPORT_EMAILED_FALLBACK, severity: 'info' },
            toastKeyRef.current
          );
          return;
        }

        if (result?.url) {
          // The URL is fed straight into an anchor's href, so check the scheme
          // before following it rather than trusting the response outright.
          if (!isSafeUrl(result.url)) {
            report(EXPORT_STATUS.failed, toastKeyRef.current);
            return;
          }
          downloadFile(result.url, `${input.fileName}.${fileType}`);
          report(EXPORT_STATUS.success, toastKeyRef.current);
        } else {
          report(EXPORT_STATUS.completed, toastKeyRef.current);
        }
      } catch {
        report(EXPORT_STATUS.failed, toastKeyRef.current);
      } finally {
        inFlightRef.current = false;
      }
    },
    [mutateAsync]
  );

  return {
    exportAnchor,
    setExportAnchor,
    handleExport,
    isExporting: exportMutation.isPending,
  };
};
